import { type ChangeEvent } from "react";

export const roundToNearest10 = (value: number) => Math.round(value / 10) * 10;

// Round to N significant figures so the step scales with the value's magnitude
// (e.g. 9970 -> 10000, 18280 -> 18000 at 2 figures). `minStep` enforces a floor
// on the rounding step so small values still snap to a sensible unit
// (e.g. with minStep 10, 47 -> 50). Keeps labels tidy across small and large
// price ranges.
export const roundToSignificantFigures = (
  value: number,
  figures = 2,
  minStep = 1
) => {
  if (value === 0) return 0;
  const magnitude = Math.floor(Math.log10(Math.abs(value)));
  const step = Math.max(minStep, Math.pow(10, magnitude - figures + 1));
  return Math.round(value / step) * step;
};

export const addThousandsSeparator = (value?: number | string | null) =>
  value !== null && value !== undefined
    ? value
        .toString()
        .replace(/[^\d]+/g, "")
        .replace(/\B(?=(\d{3})+(?!\d))/g, " ")
    : "";

export const removeThousandsSeparator = (value?: string) =>
  value !== undefined && !isNaN(Number.parseInt(value.replace(/\D/g, ""))) // NaN if value is entirely non-numeric
    ? value.replace(/\D/g, "")
    : "";

const getNewCursorPositionBidInput = (
  valueBeforeFormatting: string,
  formattedValue: string,
  cursorPosition: number | null
): number => {
  if (cursorPosition === null) return 0;

  // Count the number of digits before the cursor in the unformatted value
  const digitsBeforeCursor = valueBeforeFormatting
    .slice(0, cursorPosition)
    .split("")
    .reduce((count, char) => (/\d/.test(char) ? count + 1 : count), 0);

  // Find the new cursor position in the formatted value
  let digitsCounted = 0;
  const newCursorPosition = formattedValue.split("").findIndex((char) => {
    if (/\d/.test(char)) {
      digitsCounted++;
    }
    return digitsCounted === digitsBeforeCursor;
  });

  // If the cursor is at the end of the input, return the length of the formatted value
  return newCursorPosition === -1
    ? formattedValue.length
    : newCursorPosition + 1;
};

const updateCursorPosition = (
  inputElement: HTMLInputElement | HTMLTextAreaElement,
  newCursorPosition: number
) => {
  window.requestAnimationFrame(() => {
    inputElement.selectionStart = newCursorPosition;
    inputElement.selectionEnd = newCursorPosition;
  });
};

// Reposition the cursor when user types in the input field
export const handlePositionCursorInput = (
  e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  setInputValue: (value: string) => void
) => {
  const cursorPosition = e.target.selectionStart;
  const inputElement = e.target;
  const valueBeforeFormatting = e.target.value;

  // Format the value with thousands separator
  const formattedValue = addThousandsSeparator(valueBeforeFormatting);

  // Calculate the new cursor position
  const newCursorPosition = getNewCursorPositionBidInput(
    valueBeforeFormatting,
    formattedValue,
    cursorPosition
  );

  updateCursorPosition(inputElement, newCursorPosition);
  setInputValue(formattedValue);

  return setInputValue;
};
